import { add } from 'date-fns'
import { Cookies } from 'react-cookie'

type CallbackFuncCount = (count: number) => void
type CallbackFuncExists = (exists: boolean) => void
type UnsubscribeFunc = () => void

class WishListStateManager {
  private channel: BroadcastChannel | null = null

  private cookies: Cookies

  private count: number = 0

  private keys: Set<string>

  private subscribersCount: Set<CallbackFuncCount>

  private subscribersExists: Map<string, Set<CallbackFuncExists>>

  constructor() {
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      this.channel = new BroadcastChannel('wishlist-sync')

      this.channel.addEventListener('message', (event) => {
        switch (event.data?.type) {
          case 'RELOAD':
            if (this.load() === 0) {
              // sometimes the data is not immediately available, retry a bit later
              setTimeout(() => { this.load() }, 100)
            }

            return
          default:
            console.warn('[WishListStateManager]: unhandled message type', event.data?.type)
            return
        }
      })
    }

    this.count = 0
    this.keys = new Set<string>()
    this.subscribersCount = new Set<CallbackFuncCount>()
    this.subscribersExists = new Map<string, Set<CallbackFuncExists>>()
    this.load()
  }

  clear(): void {
    this.keys?.forEach((key: string) => {
      this.subscribersExists.get(key)?.forEach(callback => callback(false))
    })
    this.keys.clear()
    this.save()
  }

  getKeys(): string[] {
    return Array.from(this.keys)
  }

  load(): number {
    let differences = 0
    this.cookies = new Cookies()
    const nextKeys = new Set<string>()

    this.cookies.get<string[]>('th_wishlist')?.forEach((key) => {
      nextKeys.add(key)

      if (this.keys.has(key)) {
        return
      }

      differences += 1
      this.subscribersExists.get(key)?.forEach(callback => callback(true))
    })

    this.keys.forEach((key: string) => {
      if (nextKeys.has(key)) {
        return
      }

      differences += 1
      this.subscribersExists.get(key)?.forEach(callback => callback(false))
    })

    this.keys = nextKeys

    if (this.keys.size !== this.count) {
      this.count = this.keys.size
      this.subscribersCount.forEach(callback => callback(this.count))
    }

    return differences
  }

  save(): void {
    setTimeout(() => {
      this.cookies.set('th_wishlist', Array.from(this.keys), {
        expires: add(new Date(), { days: 100 }),
        path: '/',
      })
      this.channel?.postMessage({ type: 'RELOAD' })
    }, 0)

    if (this.keys.size !== this.count) {
      this.count = this.keys.size
      this.subscribersCount.forEach(callback => callback(this.count))
    }
  }

  set(key: string, result: boolean): void {
    const has = this.keys.has(key)
    if ((result && has) || (!result && !has)) {
      return
    }

    if (result) {
      this.keys.add(key)
    } else {
      this.keys.delete(key)
    }

    this.save()

    this.subscribersExists.get(key)?.forEach(callback => callback(result))
  }

  subscribeCount(callback: CallbackFuncCount): UnsubscribeFunc {
    this.subscribersCount.add(callback)

    callback(this.keys.size)

    return () => this.subscribersCount.delete(callback)
  }

  subscribeExists(key: string, callback: CallbackFuncExists): UnsubscribeFunc {
    if (!this.subscribersExists.has(key)) {
      this.subscribersExists.set(key, new Set<CallbackFuncExists>())
    }

    this.subscribersExists.get(key)?.add(callback)

    callback(this.keys.has(key))

    return () => this.subscribersExists.get(key)?.delete(callback)
  }

  toggle(key: string): boolean {
    if (this.keys.has(key)) {
      this.keys.delete(key)
    } else {
      this.keys.add(key)
    }

    this.save()

    const result = this.keys.has(key)
    this.subscribersExists.get(key)?.forEach(callback => callback(result))
    return result
  }
}

export const wishListStateManager = new WishListStateManager()
